/**
 * User hover card component
 * Shows user profile information on hover
 */

import { useNavigate } from 'react-router-dom'
import { MessageSquare, Mail, MapPin, Clock } from 'lucide-react'
import { NodeAvatar } from '@/components/nodes/NodeAvatar'
import { NodeName } from '@/components/nodes/NodeName'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { User } from '@/types'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { UserStatusDisplay } from './UserStatusDisplay'

interface UserHoverCardProps {
  user: User
  children: React.ReactNode
}

interface CopyableInfoItemProps {
  icon: React.ReactNode
  label: string
  value: string
}

function CopyableInfoItem({ icon, label, value }: CopyableInfoItemProps) {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      toast.success(`${label} copied to clipboard`)
    } catch {
      toast.error(`Failed to copy ${label.toLowerCase()}`)
    }
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={handleCopy}
            className="flex items-center text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
          >
            {icon}
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            <span className="font-bold">{label}:</span> {value}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export function UserHoverCard({ user, children }: UserHoverCardProps) {
  const { user: currentUser } = useAuth()
  const navigate = useNavigate()
  const isCurrentUser = currentUser?.id === user.id

  const handleMessageClick = () => {
    navigate(`/messages/new?user=${user.id}`)
  }

  const handleViewProfileClick = () => {
    navigate(`/users/${user.id}`)
  }

  return (
    <HoverCard>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent className="w-80">
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <NodeAvatar node={user} size="xl" />
            <div className="flex flex-col flex-1">
              <NodeName name={user.name} textSize="lg" fontWeight="semibold" />
              <UserStatusDisplay user={user} size="sm" />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <CopyableInfoItem
              icon={<Mail className="h-4 w-4" />}
              label="Email"
              value={user.email}
            />
            {user.location && (
              <CopyableInfoItem
                icon={<MapPin className="h-4 w-4" />}
                label="Location"
                value={user.location}
              />
            )}
            <CopyableInfoItem
              icon={<Clock className="h-4 w-4" />}
              label="Timezone"
              value={user.timezone}
            />
          </div>

          <div className="flex gap-2">
            {!isCurrentUser && (
              <Button size="sm" className="flex-1" onClick={handleMessageClick}>
                <MessageSquare className="h-4 w-4 mr-2" />
                Message
              </Button>
            )}
            <Button size="sm" variant="outline" className="flex-1" onClick={handleViewProfileClick}>
              View Profile
            </Button>
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  )
}
